import { useGoogleRecaptcha } from '@/lib/recaptcha'
import { Location, MapOptions, Step } from '@/models'
import axios, { AxiosError } from 'axios'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

const { getRecaptchaToken } = useGoogleRecaptcha()

export enum DriveRequestInviteError {
    alreadyRegistered,
    notEligible,
    invalidInput,
    other,
}

export const useDriveStore = defineStore('drive', () => {
    const recaptchaToken = ref<string>()
    const firstName = ref<string>()
    const lastName = ref<string>()
    const email = ref<string>()
    const phone = ref<string>()
    const licensePlate = ref<string>()
    const locations = ref<Array<Location>>()
    const location = ref<Location>()
    const requestInviteError = ref<DriveRequestInviteError>()
    const isInviteRequested = ref<boolean>(false)

    const step = computed(() => {
        if (requestInviteError.value !== undefined) {
            return Step.error
        } else if (!licensePlate.value || !email.value) {
            return Step.userData
        } else if (!isInviteRequested.value) {
            return Step.location
        } else {
            return Step.confirmation
        }
    })

    const mapOptions: MapOptions = {
        centerLat: 52.1009,
        centerLng: 5.6462,
    }

    const formattedRequestInviteError = computed(() => {
        switch (requestInviteError.value) {
            case DriveRequestInviteError.alreadyRegistered:
                return 'Je hebt je reeds aangemeld voor de rijtraining. Onze partner neemt binnenkort contact met je op om de training in te plannen.'
            case DriveRequestInviteError.notEligible:
                return 'Helaas kom je niet (meer) in aanmerking voor een rijtraining. Klopt dit volgens jou niet? Neem dan contact op met je CUPRA Specialist.'
            case DriveRequestInviteError.invalidInput:
                return 'De ingevoerde gegevens zijn ongeldig. Check je gegevens en probeer opnieuw.'
            default:
                return 'Er ging helaas iets mis. Probeer het op een later moment nog eens.'
        }
    })

    async function retrieveLocations() {
        if (locations.value) {
            return
        }

        const { data: response } = await axios.get('/api/cupra-garages')
        locations.value = response
    }

    async function requestInvite() {
        if (!licensePlate.value || !email.value || !location.value) {
            requestInviteError.value = DriveRequestInviteError.invalidInput
            return
        }

        try {
            recaptchaToken.value = await getRecaptchaToken('request_drive_invite')
        } catch (e: unknown) {
            console.error(e)
            requestInviteError.value = DriveRequestInviteError.other
            return
        }

        try {
            await axios.post(`/api/drive-invites`, {
                recaptcha_token: recaptchaToken.value,
                first_name: firstName.value,
                last_name: lastName.value,
                email: email.value,
                phone: phone.value,
                license_plate: licensePlate.value,
                cupra_garage_id: location.value.id,
            })
            isInviteRequested.value = true
        } catch (e: unknown) {
            if (e instanceof AxiosError) {
                switch (e.status) {
                    case 403:
                        requestInviteError.value = DriveRequestInviteError.notEligible
                        break
                    case 409:
                        requestInviteError.value = DriveRequestInviteError.alreadyRegistered
                        break
                    case 422:
                        requestInviteError.value = DriveRequestInviteError.invalidInput
                        break
                    default:
                        requestInviteError.value = DriveRequestInviteError.other
                        break
                }
            } else {
                requestInviteError.value = DriveRequestInviteError.other
            }
        }
    }

    function $reset() {
        isInviteRequested.value = false
        requestInviteError.value = undefined

        locations.value = undefined

        firstName.value = undefined
        lastName.value = undefined
        email.value = undefined
        phone.value = undefined
        licensePlate.value = undefined
    }

    retrieveLocations()

    return {
        step,
        firstName,
        lastName,
        email,
        phone,
        licensePlate,
        locations,
        mapOptions,
        location,
        retrieveLocations,
        requestInvite,
        requestInviteError,
        formattedRequestInviteError,
        isInviteRequested,
        $reset,
    }
})
